import { useState } from 'react'

import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LanguageIcon from '@mui/icons-material/Language'
import { IconButton, ListSubheader, Menu, MenuItem, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { Language } from 'app/models/scribe.models'

type LanguageSelectorProps = Readonly<{
  backgroundColor: string
}>

export const LanguageSelector = ({ backgroundColor }: LanguageSelectorProps) => {
  const { t, i18n } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const opened = anchor !== null
  const selectedLanguage = i18n.resolvedLanguage ?? 'en'

  const selectLanguage = (lang: Language) => {
    i18n.changeLanguage(lang)
    setAnchor(null)
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={({ currentTarget }) => setAnchor(currentTarget)}
        disableRipple
        disableFocusRipple
        sx={{ ':hover': { background: 'none' }, gap: 1 }}
        data-testid="language-selector"
      >
        <LanguageIcon fontSize="small" sx={{ color: 'secondary.main' }} />
        <Typography color="text.secondary" sx={{ fontWeight: 'bold', fontSize: 'small' }}>
          {selectedLanguage.toUpperCase()}
        </Typography>
        <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'secondary.main' }} />
      </IconButton>
      <Menu
        open={opened}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        disableScrollLock
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: { sx: { backgroundColor, border: '1px solid', borderColor: 'tertiary.main' } },
        }}
      >
        <ListSubheader
          sx={{
            lineHeight: 2,
            backgroundColor: 'inherit',
            fontSize: 'small',
          }}
        >
          <Typography variant="subtitle1" color="text.disabled">
            {t('applicationBar.languageSelector.title')}
          </Typography>
        </ListSubheader>
        {Object.values(Language).map((lang) => {
          const selected = lang === selectedLanguage

          return (
            <MenuItem
              key={lang}
              selected={selected}
              aria-selected={selected}
              onClick={() => selectLanguage(lang)}
              data-testid={lang}
              sx={{
                justifyContent: 'space-between',
                gap: 2,
                color: selected ? 'accent.main' : 'text.secondary',
                fontWeight: 'bold',
                fontSize: 'medium',
                ':hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
              }}
            >
              {t(`applicationBar.languageSelector.${lang}`)}
              {selected && <CheckIcon fontSize="small" sx={{ color: 'accent.main' }} />}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
